import { FC, useState } from 'react';
import styles from './Action.module.css';
import cn from 'classnames';
import ConfirmDeleteModal from '../../organisms/ConfirmDeleteModal';

export type ActionType = 'edit' | 'delete' | 'logout' | 'download';
export type ActionSize = 'xs' | 'sm' | 'md' | 'lg';
export type ActionColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'light'
  | 'dark';

interface ActionProps {
  type?: ActionType;
  color?: ActionColor;
  size?: ActionSize;
  itemName?: string | null;
  onClick?: () => void;
}

const Action: FC<ActionProps> = ({
  type = 'edit',
  color = 'primary',
  size = 'md',
  itemName,
  onClick,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleClick = () => {
    if (type === 'delete') {
      setShowConfirm(true);
      return;
    }
    onClick?.();
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    onClick?.();
  };

  return (
    <>
      <button
        className={cn(styles[`action-button`], styles[`action-${size}`])}
        aria-label={type}
        onClick={handleClick}
      >
        {type === 'edit' && (
          <svg
            className={styles[`action-${color}`]}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            data-testid="edit-svg"
          >
            <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
            <path
              fillRule="evenodd"
              d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a.5.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
            />
          </svg>
        )}

        {type === 'delete' && (
          <svg
            className={styles[`action-${color}`]}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            data-testid="delete-svg"
          >
            <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5" />
          </svg>
        )}

        {type === 'logout' && (
          <svg
            className={styles[`action-${color}`]}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            data-testid="logout-svg"
          >
            <path d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0z" />
            <path d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708z" />
          </svg>
        )}

        {type === 'download' && (
          <svg
            className={styles[`action-${color}`]}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            data-testid="edit-svg"
          >
            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
          </svg>
        )}
      </button>
      {type === 'delete' && (
        <ConfirmDeleteModal
          show={showConfirm}
          itemName={itemName}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  );
};

export default Action;
